import { useId, useState } from "react";
import { Box, Collapse, Link, Stack, Typography } from "@mui/material";

/**
 * One sentence, and the rest one click in.
 *
 * ## The problem this exists to solve
 *
 * Every screen in this repo has to say why you are seeing what you are
 * seeing, what a number is of, and what it does not prove. Those rules are
 * right and they are the product's argument. What they produced, left
 * unstyled, was a page where the first thing a person met was a hundred-word
 * paragraph about `manifest ∩ user`, and the chart they came for started
 * below the fold.
 *
 * A qualification nobody reads is not a qualification. Density is not the
 * same as honesty, and a wall of prose is the failure mode of a product that
 * takes honesty seriously — it buries the true sentence among nine other true
 * sentences.
 *
 * So the rule is **high level first, detail one click in**, and this is the
 * one control that implements it. Not an accordion, not a modal, not a
 * tooltip: a summary that is a complete sentence on its own, and a text
 * control that opens the rest in place.
 *
 * ## Why in place, and why not a tooltip
 *
 * In place, because the reason to read the detail is to compare it with the
 * thing beside it. Detail on another screen is detail nobody compares.
 *
 * Not a tooltip, because a tooltip is unreachable on a touch device, cannot
 * be copied, and vanishes when you look away — which is fine for a six-word
 * gloss on a chip and wrong for a paragraph somebody is meant to weigh.
 * Tooltips stay for the gloss; this is for the paragraph.
 *
 * ## The summary is not a teaser
 *
 * `summary` has to be true and complete by itself, because most people will
 * read only that. "Governed by one declared action, not by tags" is a
 * summary; "Important information about permissions" is a label pretending to
 * be one. If the short version cannot be written, the detail is not ready to
 * be collapsed.
 */
export default function Disclose({
  label = "Why",
  summary,
  count,
  children,
  align = "inline",
  sx,
}) {
  const [open, setOpen] = useState(false);
  const id = useId();

  const control = (
    <Link
      component="button"
      type="button"
      variant="body2"
      underline="hover"
      onClick={() => setOpen((was) => !was)}
      aria-expanded={open}
      aria-controls={id}
      sx={{
        // Baseline-aligned with the summary beside it rather than sitting a
        // pixel low, which is what a button next to text does by default.
        verticalAlign: "baseline",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {open ? "Less" : label}
      {count ? ` (${count})` : ""}
      {/* A caret rather than a chevron glyph from an icon font: one
          character, no dependency, and it rotates rather than swapping,
          so the control does not change width when it changes state. */}
      <Box
        component="span"
        aria-hidden
        sx={{
          display: "inline-block",
          ml: 0.5,
          fontSize: "0.7em",
          transform: open ? "rotate(90deg)" : "none",
          transition: "transform 120ms",
          // `prefers-reduced-motion` is honoured rather than assumed
          // irrelevant because the movement is small.
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        ▶
      </Box>
    </Link>
  );

  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      {align === "inline" && summary ? (
        // One paragraph, with the control as its last word. A control on its
        // own line below a one-line summary doubles the height of the
        // smallest thing on the page.
        <Typography variant="body2" color="text.secondary">
          {summary}{" "}
          {control}
        </Typography>
      ) : (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "baseline", flexWrap: "wrap", minWidth: 0 }}
        >
          {summary ? (
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
              {summary}
            </Typography>
          ) : null}
          {control}
        </Stack>
      )}

      <Collapse in={open} id={id}>
        <Box sx={{ mt: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}
